import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ApiError, API_BASE_URL } from "../../../services/apiClient";
import { useAuth } from "../../../providers/AuthProvider";
import { PageShell } from "../../shared/layouts/PageShell";
import { SurfaceCard } from "../../shared/ui/surface/SurfaceCard";
import { InfoBanner } from "../../../components/InfoBanner";
import { Breadcrumbs } from "../../../components/Breadcrumbs";
import type { BreadcrumbItem } from "../../../components/Breadcrumbs";
import { useBreadcrumbBack } from "../../../components/useBreadcrumbBack";
import { PageHeader } from "../../shared/ui/layouts/PageHeader";
import { ConnectionStatusPill } from "../../messaging/components/ConnectionStatusPill";
import { MessageBubble } from "../../messaging/components/MessageBubble";
import { MessageComposer } from "../../messaging/components/MessageComposer";
import {
  fetchSupportConversation,
  fetchSupportMessages,
  markSupportRead,
  sendSupportMessage,
  type SupportConversation,
  type SupportMessage
} from "../api/support";
import { applyReadReceipt, getLatestMessage, upsertMessages, type SupportMessageUpdate } from "../../messaging/utils/messages";
import { validateMessageContent } from "../../messaging/utils/sanitize";
import {
  ArrowLeft,
  ChatCenteredDots
} from "@phosphor-icons/react";
import { createLogger } from "../../../utils/logger";
import { defaultIconProps } from "../../shared/ui/icons";

const logger = createLogger("SupportConversationPage");

type ConnectionState = "connecting" | "open" | "closed";

export function SupportConversationPage() {
  const { t } = useTranslation();
  const { user, accessToken } = useAuth();
  const isEmailVerified = Boolean(user?.email_verified);
  const breadcrumbItems = useMemo<BreadcrumbItem[]>(
    () => [{ label: t("support.title", "Customer service"), to: "/support" }],
    [t]
  );
  const handleBack = useBreadcrumbBack({
    items: breadcrumbItems,
    includeHome: true,
    fallback: { to: "/dashboard" }
  });
  const [conversation, setConversation] = useState<SupportConversation | null>(
    null
  );
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [connectionState, setConnectionState] =
    useState<ConnectionState>("connecting");
  const socketRef = useRef<WebSocket | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const headerActions = (
    <div className="flex flex-wrap items-center gap-2">
      <a
        href="mailto:support@lotlocator.ph"
        className="pm-btn pm-btn-tonal pm-btn-md"
      >
        <ChatCenteredDots {...defaultIconProps} aria-hidden />
        {t("support.email_fallback", "Email support")}
      </a>
      <button
        type="button"
        className="pm-btn pm-btn-ghost pm-btn-md"
        onClick={handleBack}
      >
        <ArrowLeft {...defaultIconProps} aria-hidden />
        {t("actions.back", "Back")}
      </button>
    </div>
  );

  const participantLabel = useMemo(() => {
    if (!conversation || !user) {
      return "";
    }
    const supportAgent =
      conversation.participants.find((p) => p.role === "support") ??
      conversation.participants.find((p) => p.user_id !== user.id);
    if (!supportAgent) {
      return t("support.agent_label", "Customer service");
    }
    return supportAgent.name || supportAgent.email || t("support.agent_label");
  }, [conversation, user, t]);

  const markLatestAsRead = useCallback(
    async (token: string, conversationId: string, latestMessages: SupportMessage[]) => {
      const newest = getLatestMessage(latestMessages);
      if (!newest) return;
      try {
        await markSupportRead(token, conversationId, newest.id);
      } catch (readError) {
        logger.warn("Failed to mark support conversation as read", readError);
      }
    },
    []
  );

  const connectWebSocket = useCallback(
    (token: string, conversationId: string) => {
      socketRef.current?.close();
      const wsUrl = buildWebSocketUrl(
        `/support/conversations/${conversationId}/ws?token=${encodeURIComponent(token)}`
      );
      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;
      setConnectionState("connecting");
      socket.onopen = () => setConnectionState("open");
      socket.onclose = () => setConnectionState("closed");
      socket.onerror = () => setConnectionState("closed");
      socket.onmessage = async (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload?.type === "message.created" && payload.message) {
            const incoming = payload.message as SupportMessage;
            setMessages((prev) => upsertMessages(prev, [incoming]));
            await markLatestAsRead(token, conversationId, [incoming]);
          }
          if (payload?.type === "message.updated" && Array.isArray(payload.messages)) {
            setMessages((prev) =>
              upsertMessages(prev, payload.messages as SupportMessageUpdate[])
            );
          }
          if (payload?.type === "read.receipt" && payload.readReceipt && user) {
            setMessages((prev) =>
              applyReadReceipt(prev, payload.readReceipt, user.id)
            );
          }
        } catch (parseError) {
          logger.warn("Failed to parse websocket payload", parseError);
        }
      };
    },
    [markLatestAsRead, user]
  );

  useEffect(() => {
    if (!accessToken || !isEmailVerified) {
      setIsLoading(false);
      return;
    }
    const token = accessToken;
    let cancelled = false;
    async function bootstrap(authToken: string) {
      setIsLoading(true);
      setError(null);
      try {
        const conv = await fetchSupportConversation(authToken);
        if (cancelled) return;
        setConversation(conv);
        const messageResponse = await fetchSupportMessages(
          authToken,
          conv.id,
          undefined,
          50
        );
        if (cancelled) return;
        const sortedMessages = upsertMessages([], messageResponse.messages);
        setMessages(sortedMessages);
        await markLatestAsRead(authToken, conv.id, sortedMessages);
        connectWebSocket(authToken, conv.id);
      } catch (err) {
        const apiError = err as ApiError;
        const message =
          apiError?.body?.message ||
          apiError?.message ||
          t("support.load_error", "Unable to load support conversation.");
        setError(message);
        setConnectionState("closed");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    bootstrap(token);
    return () => {
      cancelled = true;
      socketRef.current?.close();
    };
  }, [accessToken, t, markLatestAsRead, connectWebSocket, isEmailVerified]);

  useEffect(() => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollTo({
      top: scrollerRef.current.scrollHeight,
      behavior: "smooth"
    });
  }, [messages.length]);

  const handleSend = async (event?: FormEvent) => {
    if (event) {
      event.preventDefault();
    }
    if (!accessToken || !conversation || !isEmailVerified) return;
    const { content, errorKey } = validateMessageContent(inputValue);
    if (errorKey) {
      setError(t(errorKey));
      setInputValue(content);
      return;
    }
    if (!content) return;

    const token = accessToken;
    setIsSending(true);
    try {
      const message = await sendSupportMessage(token, conversation.id, content);
      setInputValue("");
      setMessages((prev) => upsertMessages(prev, [message]));
      await markLatestAsRead(token, conversation.id, [message]);
    } catch (err) {
      const apiError = err as ApiError;
      setError(
        apiError?.body?.message || t("support.send_error", "Unable to send your message.")
      );
    } finally {
      setIsSending(false);
    }
  };

  if (!user) {
    return null;
  }

  if (!isEmailVerified) {
    return (
      <PageShell width="wide">
        <div className="flex flex-col gap-4">
          <Breadcrumbs items={breadcrumbItems} />
          <PageHeader
            title={t("support.title", "Customer service")}
            subtitle={t("support.subtitle", "Message our team for help with your account or surveys.")}
            actions={headerActions}
          />
          <InfoBanner
            tone="info"
            prominent
            title={t("support.email_verification_required.title", "Verify your email to chat with support")}
            description={t("support.email_verification_required.description", "Confirm your email so we can secure your support conversation. You can still email us at support@lotlocator.ph.")}
            icon={<ChatCenteredDots size={20} aria-hidden />}
          >
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to="/settings/profile#profile-email"
                className="pm-btn pm-btn-primary pm-btn-sm"
              >
                {t("support.email_verification_required.cta", "Verify email")}
              </Link>
              <a
                href="mailto:support@lotlocator.ph"
                className="pm-btn pm-btn-ghost pm-btn-sm"
              >
                {t("support.email_fallback", "Email support")}
              </a>
            </div>
          </InfoBanner>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell width="wide">
      <div className="flex flex-col gap-4">
        <Breadcrumbs items={breadcrumbItems} />
        <PageHeader
          title={t("support.title", "Customer service")}
          subtitle={t("support.subtitle", "Message our team for help with your account or surveys.")}
          actions={headerActions}
        />

        {error ? (
          <InfoBanner
            tone="info"
            title={t("support.load_error", "Unable to load support conversation.")}
            description={error}
            icon={<ChatCenteredDots size={16} aria-hidden />}
          >
            <button
              type="button"
              className="pm-btn pm-btn-ghost pm-btn-sm"
              onClick={handleBack}
            >
              <ArrowLeft {...defaultIconProps} aria-hidden />
              {t("actions.back", "Back")}
            </button>
          </InfoBanner>
        ) : null}
        <SurfaceCard className="flex min-h-[520px] flex-col gap-3 bg-white/80 p-4 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900/70 dark:ring-slate-800">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-50">
                {t("support.thread_title", "Chat with support")}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {participantLabel}
              </p>
            </div>
            <ConnectionStatusPill
              state={connectionState}
              label={t(`support.connection.${connectionState}`)}
            />
          </div>

          <div
            ref={scrollerRef}
            className="pm-scrollbar flex-1 overflow-y-auto rounded-xl bg-slate-50/80 px-3 py-4 dark:bg-slate-800/60"
          >
            {isLoading ? (
              <div className="flex h-full items-center justify-center">
                <InlineSpinner />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-slate-500 dark:text-slate-300">
                <ChatCenteredDots size={28} aria-hidden />
                <p className="text-sm">{t("support.empty", "Ask us anything about LotLocator.")}</p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {messages.map((message) => {
                  const isMe = user?.id === message.sender_id;
                  const statusLabel = isMe
                    ? t(`support.delivery.${message.delivery_state}`, message.delivery_state)
                    : undefined;
                  return (
                    <MessageBubble
                      key={message.id}
                      content={message.content}
                      timestamp={message.created_at}
                      isOwn={isMe}
                      deliveryState={
                        isMe
                          ? (message.delivery_state as "pending" | "sent" | "delivered" | "read")
                          : undefined
                      }
                      deliveryLabel={statusLabel}
                    />
                  );
                })}
              </div>
            )}
          </div>

          <MessageComposer
            value={inputValue}
            onChange={setInputValue}
            onSend={() => void handleSend()}
            placeholder={t("support.message_input.placeholder", "Type your message...")}
            label={t("support.message_input.label", "Message support")}
            sendLabel={t("support.send", "Send")}
            inputId="support-message"
            isSending={isSending}
            disabled={!conversation}
          />
        </SurfaceCard>
      </div>
    </PageShell>
  );
}

function buildWebSocketUrl(path: string) {
  const url = new URL(API_BASE_URL);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return `${url.origin}${url.pathname}${path}`;
}

function InlineSpinner() {
  return (
    <span
      className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600 dark:border-slate-700 dark:border-t-slate-200"
      aria-hidden
    />
  );
}
